import { BUTTON } from '@lumapps/wrex-button/constants';
import { BUTTON_ELEMENTS as BUTTON_SERIALIZE } from '@lumapps/wrex-button/plugin/html/serialize';
import { CODE_BLOCK } from '@lumapps/wrex-code-block/constants';
import { COLUMN, COLUMNS } from '@lumapps/wrex-columns/constants';
import { COLUMNS_ELEMENTS as COLUMNS_SERIALIZE } from '@lumapps/wrex-columns/plugin/html/serialize';
import { EMBEDDED_MAP } from '@lumapps/wrex-embedded-map/constants';
import { EMBEDDED_MAP_ELEMENTS as EMBEDDED_MAP_SERIALIZE } from '@lumapps/wrex-embedded-map/plugin/html/serialize';
import { EMBEDDED_VIDEO } from '@lumapps/wrex-embedded-video/constants';
import { EMBEDDED_VIDEO_ELEMENTS as EMBEDDED_VIDEO_SERIALIZE } from '@lumapps/wrex-embedded-video/plugin/html/serialize';
import { ALIGNED_IMAGE_WRAPPER, IMAGE_GROUP } from '@lumapps/wrex-enhanced-image/constants';
import { ENHANCED_IMAGE_ELEMENTS as ENHANCED_IMAGE_SERIALIZE } from '@lumapps/wrex-enhanced-image/plugin/html/serialize';
import { FILE } from '@lumapps/wrex-file/constants';
import { FILE_ELEMENTS as FILE_SERIALIZE } from '@lumapps/wrex-file/plugin/html/serialize';
import { IFRAME } from '@lumapps/wrex-iframe/constants';
import { IFRAME_ELEMENTS as IFRAME_SERIALIZE } from '@lumapps/wrex-iframe/plugin/html/serialize';
import { LINK_PREVIEW } from '@lumapps/wrex-link-preview/constants';
import { LINK_PREVIEW_ELEMENTS as LINK_PREVIEW_SERIALIZE } from '@lumapps/wrex-link-preview/plugin/html/serialize';
import { LINK } from '@lumapps/wrex-link/constants';
import { LINK_ELEMENTS as LINK_SERIALIZE } from '@lumapps/wrex-link/plugin/html/serialize';
import { LIST_ITEM, ORDERED_LIST, UNORDERED_LIST } from '@lumapps/wrex-list/constants';
import { LIST_ELEMENTS as LIST_SERIALIZE } from '@lumapps/wrex-list/plugin/html/serialize';
import { PLAY_VIDEO } from '@lumapps/wrex-play-video/constants';
import { PLAY_VIDEO_ELEMENTS as PLAY_VIDEO_SERIALIZE } from '@lumapps/wrex-play-video/plugin/html/serialize';
import { QUOTE_BLOCK } from '@lumapps/wrex-quote/constants';
import { QUOTE_ELEMENTS as QUOTE_SERIALIZE } from '@lumapps/wrex-quote/plugin/html/serialize';
import { T_D, T_R, TABLE } from '@lumapps/wrex-table/constants';
import { TABLE_ELEMENTS as TABLE_SERIALIZE } from '@lumapps/wrex-table/plugin/html/serialize';
import {
    BOLD,
    COLORED,
    DIVIDER,
    HEADING,
    HEADLINE,
    INLINE_CODE,
    ITALIC,
    PARAGRAPH,
    STRIKETHROUGH,
    SUBTITLE,
    TITLE,
    UNDERLINE,
} from '@lumapps/wrex-typography/constants';
import {
    TYPOGRAPHY_ELEMENTS as TYPOGRAPHY_SERIALIZE,
    TYPOGRAPHY_MARKS as MARKS_SERIALIZE,
} from '@lumapps/wrex-typography/plugin/html/serialize';
import { USER_MENTION } from '@lumapps/wrex-user-mention/constants';
import { USER_MENTION_ELEMENTS as USER_MENTION_SERIALIZE } from '@lumapps/wrex-user-mention/plugin/html/serialize';

import { HTMLSerializeOptions } from './toHTML/types';

const HTML_SERIALIZATION_ELEMENTS = {
    [PARAGRAPH]: TYPOGRAPHY_SERIALIZE[PARAGRAPH],
    [HEADING]: TYPOGRAPHY_SERIALIZE[HEADING],
    [HEADLINE]: TYPOGRAPHY_SERIALIZE[HEADLINE],
    [TITLE]: TYPOGRAPHY_SERIALIZE[TITLE],
    [CODE_BLOCK]: TYPOGRAPHY_SERIALIZE[CODE_BLOCK],
    [INLINE_CODE]: TYPOGRAPHY_SERIALIZE[INLINE_CODE],
    [SUBTITLE]: TYPOGRAPHY_SERIALIZE[SUBTITLE],
    [DIVIDER]: TYPOGRAPHY_SERIALIZE[DIVIDER],
    [UNORDERED_LIST]: LIST_SERIALIZE[UNORDERED_LIST],
    [ORDERED_LIST]: LIST_SERIALIZE[ORDERED_LIST],
    [LIST_ITEM]: LIST_SERIALIZE[LIST_ITEM],
    [IMAGE_GROUP]: ENHANCED_IMAGE_SERIALIZE[IMAGE_GROUP],
    [ALIGNED_IMAGE_WRAPPER]: ENHANCED_IMAGE_SERIALIZE[ALIGNED_IMAGE_WRAPPER],
    [USER_MENTION]: USER_MENTION_SERIALIZE[USER_MENTION],
    [LINK_PREVIEW]: LINK_PREVIEW_SERIALIZE[LINK_PREVIEW],
    [LINK]: LINK_SERIALIZE[LINK],
    [TABLE]: TABLE_SERIALIZE[TABLE],
    [T_R]: TABLE_SERIALIZE[T_R],
    [T_D]: TABLE_SERIALIZE[T_D],
    [QUOTE_BLOCK]: QUOTE_SERIALIZE[QUOTE_BLOCK],
    [IFRAME]: IFRAME_SERIALIZE[IFRAME],
    [EMBEDDED_MAP]: EMBEDDED_MAP_SERIALIZE[EMBEDDED_MAP],
    [EMBEDDED_VIDEO]: EMBEDDED_VIDEO_SERIALIZE[EMBEDDED_VIDEO],
    [PLAY_VIDEO]: PLAY_VIDEO_SERIALIZE[PLAY_VIDEO],
    [FILE]: FILE_SERIALIZE[FILE],
    [BUTTON]: BUTTON_SERIALIZE[BUTTON],
    [COLUMNS]: COLUMNS_SERIALIZE[COLUMNS],
    [COLUMN]: COLUMNS_SERIALIZE[COLUMN],
} as const;

const HTML_SERIALIZATION_MARKS = {
    [BOLD]: MARKS_SERIALIZE[BOLD],
    [ITALIC]: MARKS_SERIALIZE[ITALIC],
    [COLORED]: MARKS_SERIALIZE[COLORED],
    [STRIKETHROUGH]: MARKS_SERIALIZE[STRIKETHROUGH],
    [UNDERLINE]: MARKS_SERIALIZE[UNDERLINE],
} as const;

export const getHtmlSerializationOptions = ({
    elements,
    marks,
}: {
    elements: readonly (keyof typeof HTML_SERIALIZATION_ELEMENTS)[];
    marks: readonly (keyof typeof HTML_SERIALIZATION_MARKS)[];
}): HTMLSerializeOptions => {
    const htmlSerializeElements = Object.fromEntries(
        elements.map((element) => [element, HTML_SERIALIZATION_ELEMENTS[element]]),
    );
    const htmlSerializeMarks = Object.fromEntries(marks.map((mark) => [mark, HTML_SERIALIZATION_MARKS[mark]]));

    return {
        elements: htmlSerializeElements,
        marks: htmlSerializeMarks,
    };
};
